import { BrButton, BrInput, BrLoading, BrSelect } from '@govbr-ds/react-components';
import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import styles from './dadosPessoaRelacionadaForm.module.scss';

import { DiefCfemContext } from '../../../../../../../../../../../contexts/DiefCfemContext';
import { ETipoRelacionamentoVendaNacional } from '../../../../../../../../../../../enums/ETipoRelacionamentoVendaNacional';
import { useNotificacao } from '../../../../../../../../../../../hooks/useNotificacao';
import {
  IDadosPessoaRelacionadaEnviar,
  IVendaNacional,
} from '../../../../../../../../../../../interfaces/IVendaNacional';
import { formatarCNPJOrCpf, unmaskSelectCpfCnpj } from '../../../../../../../../../../../utils/formatCNPJOrCPF';
import { useBackEndRequests } from '../../../../../../../../../lib/backEndRequests';
import { validarDadosPessoaRelacionada } from './dadosPessoaValidacoes/dadosPessoaRelacionadaFormValidacoes';

interface IDadosPessoaRelacionadaFormProps {
  isReadOnly: boolean;
  setIsStepNcm?: React.Dispatch<React.SetStateAction<boolean>>;
  vendaPessoaRelacionadaData: IVendaNacional | undefined;
  isLoading?: boolean;
  refetchDadosAnteriores?: () => void;
  isEdicao?: boolean;
  setIsEdicao?: React.Dispatch<React.SetStateAction<boolean>>;
}

const DadosPessoaRelacionadaForm = ({
  isReadOnly,
  setIsStepNcm,
  vendaPessoaRelacionadaData,
  isLoading,
  refetchDadosAnteriores,
  isEdicao = false,
  setIsEdicao = () => {},
}: IDadosPessoaRelacionadaFormProps) => {
  const [cnpjRelacionado, setCnpjRelacionado] = useState<string>('');
  const [razaoSocialRelacionado, setRazaoSocialRelacionado] = useState<string>('');
  const [tipoRelacionamentoComPJ, setTipoRelacionamentoComPJ] = useState<ETipoRelacionamentoVendaNacional>();

  const { postDadosPessoaRelacionada, putDadosPessoaRelacionada } = useBackEndRequests();

  const context = useContext(DiefCfemContext);
  const idOperacao = context?.idOperacaoEditar ?? '';

  useEffect(() => {
    if (vendaPessoaRelacionadaData) {
      setCnpjRelacionado(vendaPessoaRelacionadaData.cnpjRelacionado || '');
      setRazaoSocialRelacionado(vendaPessoaRelacionadaData.razaoSocialRelacionado || '');
      setTipoRelacionamentoComPJ(vendaPessoaRelacionadaData.relacionamentoComPJ as ETipoRelacionamentoVendaNacional);
    }
  }, [vendaPessoaRelacionadaData]);

  const mostrarNotificacao = useNotificacao();

  const salvarDadosPessoaRelacionada = async () => {
    if (
      !validarDadosPessoaRelacionada(
        cnpjRelacionado,
        razaoSocialRelacionado,
        tipoRelacionamentoComPJ,
        mostrarNotificacao
      )
    )
      return;

    const dadosPessoaRelacionada: IDadosPessoaRelacionadaEnviar = {
      declaracaoItemId: parseInt(idOperacao),
      cnpjRelacionado: cnpjRelacionado.replace(/[./-]/g, ''),
      razaoSocialRelacionado: razaoSocialRelacionado,
      relacionamentoComPJ: tipoRelacionamentoComPJ as ETipoRelacionamentoVendaNacional,
    };
    if (!isEdicao) {
      await postDadosPessoaRelacionada(dadosPessoaRelacionada)
        .then((res) => {
          context?.setIdVendaNacional(res.id ?? -1);
          if (setIsStepNcm) setIsStepNcm(true);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao enviar dados da pessoa jurídica relacionada. Tente novamente mais tarde.',
            ...(err && { complementoSentryId: err }),
          });
        });
    } else {
      const dadosPessoaRelacionadaComId = {
        ...dadosPessoaRelacionada,
        id: context?.idVendaNacional,
      };
      await putDadosPessoaRelacionada(dadosPessoaRelacionadaComId)
        .then((res) => {
          context?.setIdVendaNacional(res.id ?? -1);
          if (setIsStepNcm) setIsStepNcm(true);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao enviar dados da pessoa jurídica relacionada. Tente novamente mais tarde.',
            ...(err && { complementoSentryId: err }),
          });
        });
      refetchDadosAnteriores?.();
    }
  };

  const editarDadosPessoaRelacionada = () => {
    setCnpjRelacionado(vendaPessoaRelacionadaData?.cnpjRelacionado || '');
    setRazaoSocialRelacionado(vendaPessoaRelacionadaData?.razaoSocialRelacionado || '');
    setTipoRelacionamentoComPJ(vendaPessoaRelacionadaData?.relacionamentoComPJ as ETipoRelacionamentoVendaNacional);
    setIsEdicao(true);
    if (setIsStepNcm) setIsStepNcm(false);
  };

  return (
    <div className={styles.container}>
      {isLoading && <BrLoading />}
      <div className={isReadOnly ? styles.insideTitle : styles.formTitle}>
        <span>Dados da Pessoa Jurídica Relacionada</span>
      </div>

      <Row>
        <Col size={4} className={styles.inputCol}>
          <span className={styles.label}>CNPJ da Pessoa Jurídica Relacionada</span>
          {!isReadOnly ? (
            <BrInput
              data-testid='cnpj-pessoa-relacionada'
              autoComplete='off'
              onChange={(e) => setCnpjRelacionado(unmaskSelectCpfCnpj(e.target.value).slice(0, 14))}
              value={formatarCNPJOrCpf(cnpjRelacionado, true)}
            />
          ) : (
            <span>{formatarCNPJOrCpf(vendaPessoaRelacionadaData?.cnpjRelacionado || '')}</span>
          )}
        </Col>
        <Col size={4} className={styles.inputCol}>
          <span className={styles.label}>Razão Social</span>
          {!isReadOnly ? (
            <BrInput
              data-testid='razao-social-pessoa-relacionada'
              autoComplete='off'
              maxLength={200}
              onChange={(e) => setRazaoSocialRelacionado(e.target.value)}
              value={razaoSocialRelacionado}
            />
          ) : (
            <span>{vendaPessoaRelacionadaData?.razaoSocialRelacionado}</span>
          )}
        </Col>
        <Col size={4} className={styles.inputCol}>
          <span className={styles.label}>Relacionamento com a PJ</span>
          {!isReadOnly ? (
            <BrSelect
              data-testid='relacionamento-pessoa-juridica'
              options={Object.keys(ETipoRelacionamentoVendaNacional).map((key) => ({
                label: ETipoRelacionamentoVendaNacional[key as keyof typeof ETipoRelacionamentoVendaNacional],
                value: key,
              }))}
              onChange={(value: string) => setTipoRelacionamentoComPJ(value as ETipoRelacionamentoVendaNacional)}
              value={tipoRelacionamentoComPJ}
            />
          ) : (
            <span>
              {vendaPessoaRelacionadaData?.relacionamentoComPJ
                ? ETipoRelacionamentoVendaNacional[
                    (
                      vendaPessoaRelacionadaData?.relacionamentoComPJ as string
                    ).toUpperCase() as keyof typeof ETipoRelacionamentoVendaNacional
                  ]
                : ''}
            </span>
          )}
        </Col>
      </Row>
      {setIsStepNcm && (
        <div className={styles.buttonContainer}>
          {!isReadOnly ? (
            <BrButton secondary size='small' onClick={() => salvarDadosPessoaRelacionada()} icon='fas fa-save'>
              Salvar Dados Básicos
            </BrButton>
          ) : (
            <BrButton
              circle
              icon='fas fa-edit'
              onClick={() => editarDadosPessoaRelacionada()}
              data-testid='editar-dados-pessoais-btn'
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DadosPessoaRelacionadaForm;
